import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from "react-router";
import { DialogContent, Drawer, IconButton, styled } from "@mui/material";
import { ClinicDashBoardRoute } from "../../clinic/ClinicRoutes";
import { ClinicMenu } from "./clinicMenu";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { ClinicSettingManagement } from "./clinicSettingManagement";
import { ClinicTranslations } from "./clinicTranslations";
import { ImportPatient } from "./special/importMaccabiPatients/importPatients";
import {
  ClinicCustomization,
  CustomizationTabs,
} from "./clinicCustomization/clinicCustomization";
import { ClinicThemeCustomizationV2 } from "./clinicCustomization/clinicThemeCustomizationV2";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import { ClinicTopBar } from "./clinicTopBar";
import { ClinicSelectionDialog } from "./clinicSelectionDialog";
import RestrictAccess from "./elements/RestrictAccess";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { useClinicState } from "../../clinic/clinicStateContext";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { UserClinicsClinics, UserRoles } from "fe-common/graphql";
import { InviteRouter } from "./patients/invite/inviteRouter";
import { WelcomeDialog } from "./WelcomeDialog";
import { CLINIC_MANAGE_URL } from "./clinicSettings/clinicUrls";
import { PatientsTable } from "./patients/patientsTable";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { NavBarZIndex } from "fe-common/ui/stacking";
import { PageTitleContext } from "../../context/pageTitleContext";
import { useClinicUserContext } from "./clinicUserContext";
import { removeTrailingSlash } from "fe-common/routes/removeTrailingSlash";
import { ClinicDialog } from "./elements/clinicDialog";
import { NewCase } from "./patients/newCase";
import { ClinicWalkthroughProvider } from "../../clinic/clinicWalkthroughContext";

const SIDE_MENU_WIDTH = 360;

const TOP_BAR_HEIGHT = 60;
//region [[ Styles ]]
const ManageClinicView = styled("div")({
  display: "flex",
  color: ClinicColors.textMain,
  flexDirection: "row",
  height: "100%",
  overflowY: "hidden",
  overflowX: "hidden",
  ...scrollableStyle,
});

const ClinicLeft = styled(Drawer)(({ theme }) => ({
  width: SIDE_MENU_WIDTH,
  backgroundColor: "#F4F3F6",
  minHeight: "100%",
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: SIDE_MENU_WIDTH,
    boxSizing: "border-box",
    padding: 28,
    zIndex: NavBarZIndex - 2,
  },
  [theme.breakpoints.down("xs")]: {
    "& .MuiDrawer-paper": {
      padding: 18,
    },
  },
  ...scrollableStyle,
}));

const ClinicRight = styled("main")(
  ({ theme, open }: { theme?: any; open: boolean }) => ({
    backgroundColor: ClinicColors.mainBackground,
    paddingTop: TOP_BAR_HEIGHT,
    overflow: "hidden",
    flexGrow: 1,
    transition: open
      ? theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        })
      : theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
    marginLeft: open ? 0 : `-${SIDE_MENU_WIDTH}px`,
    // [theme.breakpoints.down("xs")]: {
    //   overflow: "visible",
    //   height: "auto",
    // },
  }),
);

const CloseMenuButton = styled(IconButton)({
  position: "absolute",
  top: 6,
  right: 5,
  zIndex: 400,
});

const OpenMenuButton = styled(IconButton)({
  marginLeft: -10,
});

const ClinicTop = styled("div")((props: { theme?: any; open: boolean }) => ({
  position: "fixed",
  display: "flex",
  alignItems: "center",
  top: 0,
  right: 0,
  width: props.open ? `calc(100% - ${SIDE_MENU_WIDTH}px)` : "100%",
  height: TOP_BAR_HEIGHT,
  zIndex: 200,
  borderBottom: "1px solid #E1E1E1",
  padding: "0 40px",
  [props.theme.breakpoints.down("xs")]: {
    padding: "0 10px",
  },
}));

const ClinicMain = styled("div")({
  position: "relative",
  width: "100%",
  height: "100%",
});
//endregion [[ Styles ]]

//region [[ Props ]]
export type ClinicWithFavorite = UserClinicsClinics & {
  isFavorite: boolean;
};
//endregion [[ Props ]]

//region [[ Functions ]]
export const getLastSelectedClinicId = (username: string): string =>
  localStorage.getItem("lastSelectedClinicId" + username);

export const saveLastSelectedClinicId = (
  clinicId: string,
  username: string,
) => {
  localStorage.setItem("lastSelectedClinicId" + username, clinicId);
};

export const clearLastSelectedClinicId = (username: string): void =>
  localStorage.removeItem("lastSelectedClinicId" + username);

//endregion [[ Functions ]]

export const ClinicManage = () => {
  const [showClinicSelection, setShowClinicSelection] = useState<boolean>(
    false,
  );
  const [showNewCaseDialog, setShowNewCaseDialog] = useState(false);
  const [menuOpen, setMenuOpen] = useState(true);
  const dao = useClinicContext();
  const match = useRouteMatch();
  const currURL = removeTrailingSlash(match.url);
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const {
    username,
    userClinics,
    setUserClinics,
    hasSuperAccess,
    hasAdminAccess,
    hasManagerAccess,
    loading: loadingClinicUser,
  } = useClinicUserContext();

  const [
    {
      isMasterClinic,
      isDemoClinic,
      clinicData: {
        id: clinicId,
        name: clinicName,
        displayName: clinicDisplayName,
      },
      clinicConfig,
      gotoCase,
    },
    setClinicState,
  ] = useClinicState();

  const pageTitle = useContext(PageTitleContext);

  const displayNameRef = useRef(clinicDisplayName);

  const goToClinic = useCallback(
    (id: string) => {
      if (clinicId !== id) setLoading(true);
      history.push(`${CLINIC_MANAGE_URL}/${id}`);
      if (id !== "new") {
        getCurrentUserEmail().then((email) =>
          saveLastSelectedClinicId(id, email),
        );
      } //save for next time
    },
    [history, clinicId],
  );

  const demoMode = clinicConfig?.demoMode && !hasSuperAccess;

  const toggleUserClinic = async (clinic: ClinicWithFavorite) => {
    try {
      let status;
      if (clinic.isFavorite) {
        status = await dao.removeUserFromClinic(
          clinic.id,
          username.toLowerCase(),
        );
      } else {
        status = await dao.addUserToClinic(
          clinic.id,
          username.toLowerCase(),
          UserRoles.KahunClinicManager,
        );
      }
      if (status === "success") {
        // console.log("success, updating favorite clinic list");
        setUserClinics((prevState) => {
          prevState.find(
            (c) => c.id === clinic.id,
          ).isFavorite = !clinic.isFavorite;
          return prevState;
        });
        return true;
      } else {
        console.error("Failed adding then clinic to user clinics", status);
        return false;
      }
    } catch (e) {
      console.error("Failed adding the clinic to user clinics", e);
      return false;
    }
  };

  const onCaseSaved = useCallback(
    (digitCode: string) => {
      setShowNewCaseDialog(false);
      gotoCase(digitCode);
    },
    [gotoCase],
  );

  useEffect(() => {
    //if current clinic name was changed update the name in the list
    if (displayNameRef.current !== clinicDisplayName)
      setUserClinics((prevState) => {
        const updated = [...prevState];
        updated.find((c) => c.id === clinicId).displayName = clinicDisplayName;
        displayNameRef.current = clinicDisplayName;
        return updated;
      });
    pageTitle.setPageTitle(
      (clinicDisplayName || clinicName) + " - Kahun Clinic",
    );
  }, [clinicDisplayName, clinicName, clinicId, pageTitle, setUserClinics]);

  useEffect(() => {
    setLoading(loadingClinicUser);
  }, [loadingClinicUser]);

  const redirectTo = () => {
    return !hasManagerAccess && !hasSuperAccess
      ? ClinicDashBoardRoute.Invite
      : !isMasterClinic || !hasSuperAccess
      ? ClinicDashBoardRoute.Patients
      : ClinicDashBoardRoute.Advanced;
  };

  if (loading) return <>Loading</>;

  return (
    <ManageClinicView>
      <ClinicWalkthroughProvider>
        <ClinicTop className="clinic-top" open={!demoMode && menuOpen}>
          {!menuOpen && (
            <OpenMenuButton
              aria-label="open menu"
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon />
            </OpenMenuButton>
          )}
          <ClinicTopBar
            gotoClinic={goToClinic}
            currentClinic={userClinics.find((c) => c.id === clinicId)}
            onShowClinicSelection={() => setShowClinicSelection(true)}
          />
        </ClinicTop>
        {!demoMode && (
          <ClinicLeft
            className="clinic-left"
            variant="persistent"
            anchor="left"
            open={menuOpen}
          >
            <CloseMenuButton
              aria-label="hide menu"
              onClick={() => setMenuOpen(false)}
            >
              <CloseIcon />
            </CloseMenuButton>
            <ClinicMenu onClinicSelect={() => setShowClinicSelection(true)} />
          </ClinicLeft>
        )}

        <ClinicRight className="clinic-right" open={demoMode || menuOpen}>
          <ClinicMain id="clinicMain" className="clinic-main">
            <Switch>
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.Invite}/:method?`}
                component={InviteRouter}
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.Patients}/:code?`}
                render={() => (
                  <RestrictAccess hasAccess={hasManagerAccess}>
                    <PatientsTable
                      onShowNewCase={() => setShowNewCaseDialog(true)}
                    />
                  </RestrictAccess>
                )}
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.Settings}/:tab?`}
                render={() => (
                  <RestrictAccess hasAccess={!isDemoClinic || hasAdminAccess}>
                    <ClinicCustomization />
                  </RestrictAccess>
                )}
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.Advanced}/:key?`}
                render={() => (
                  <RestrictAccess hasAccess={hasAdminAccess}>
                    <ClinicSettingManagement />
                  </RestrictAccess>
                )}
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.ThemeColors}`}
                render={() => (
                  <RestrictAccess hasAccess={hasAdminAccess}>
                    <ClinicThemeCustomizationV2 clinicId={clinicId} />
                  </RestrictAccess>
                )}
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.Translation}`}
                render={() => (
                  <RestrictAccess hasAccess={hasAdminAccess}>
                    <ClinicTranslations />
                  </RestrictAccess>
                )}
              />
              <Route
                path={`${currURL}/${ClinicDashBoardRoute.ImportPatients}`}
                render={() => (
                  <RestrictAccess hasAccess={hasManagerAccess}>
                    <ImportPatient />
                  </RestrictAccess>
                )}
              />
              <Redirect
                from={`${currURL}/${ClinicDashBoardRoute.Users}`}
                to={`${currURL}/${ClinicDashBoardRoute.Settings}/${CustomizationTabs.UserManagement}`}
                exact={true}
              />
              <Redirect to={`${currURL}/${redirectTo()}`} />
            </Switch>
          </ClinicMain>
        </ClinicRight>
        <ClinicSelectionDialog
          open={showClinicSelection}
          onClose={() => setShowClinicSelection(false)}
          userClinics={userClinics}
          goToClinic={goToClinic}
          currentClinicId={clinicId}
          onToggleUserClinic={toggleUserClinic}
        />
        {clinicConfig?.standalone.manualCaseFromSummery && showNewCaseDialog && (
          <ClinicDialog
            open={true}
            onClose={() => setShowNewCaseDialog(false)}
            closeButton={true}
            maxWidth={"lg"}
          >
            <DialogContent>
              <NewCase
                clinicId={clinicId}
                onSave={onCaseSaved}
                onError={() =>
                  setClinicState((prevState) => ({
                    ...prevState,
                    toastMsg: {
                      text: "Could not create a new case",
                      severity: "error",
                    },
                  }))
                }
              />
            </DialogContent>
          </ClinicDialog>
        )}
        {!isDemoClinic && <WelcomeDialog username={username} />}
      </ClinicWalkthroughProvider>
    </ManageClinicView>
  );
};
